import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Folder, FileText } from 'lucide-react';

/**
 * Flat-render folder tree.
 *
 * Renders the tree as a flat list to avoid babel's "Maximum call stack size
 * exceeded" error that triggers on recursive JSX components in large modules.
 *
 * Open/closed state is tracked in a Set keyed by node.path.
 */
function flatten(node, depth, openPaths, out) {
  out.push({ node, depth });
  const isOpen = depth === -1 ? true : openPaths.has(node.path);
  if (isOpen && node.children && node.children.length > 0) {
    for (const child of node.children) {
      flatten(child, depth + 1, openPaths, out);
    }
  }
}

export default function FolderTree({ tree, selectedPath, onSelect }) {
  // Open everything at the top level by default
  const initialOpen = new Set();
  if (tree?.children) {
    tree.children.forEach((c) => initialOpen.add(c.path));
  }
  const [openPaths, setOpenPaths] = useState(initialOpen);

  if (!tree) return null;

  const toggle = (path) => {
    setOpenPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const rows = [];
  // Root is always rendered separately (not part of the recursion below) so the
  // children of root start at depth 0.
  if (tree.children) {
    for (const child of tree.children) {
      flatten(child, 0, openPaths, rows);
    }
  }

  return (
    <div>
      {rows.map(({ node, depth }) => {
        const hasChildren = node.children && node.children.length > 0;
        const isOpen = openPaths.has(node.path);
        const isSelected = selectedPath === node.path;
        return (
          <div
            key={node.path}
            className={`flex items-center gap-1.5 rounded px-1.5 py-1 cursor-pointer text-sm transition-colors ${
              isSelected ? 'bg-[#1E3A8A]/10 text-[#1E3A8A]' : 'text-slate-700 hover:bg-slate-100'
            }`}
            style={{ paddingLeft: `${depth * 12 + 6}px` }}
            onClick={() => onSelect(node.path)}
            data-testid={`tree-node-${node.path}`}
          >
            {hasChildren ? (
              <button
                onClick={(e) => { e.stopPropagation(); toggle(node.path); }}
                className="w-4 h-4 flex items-center justify-center hover:bg-slate-200 rounded"
                aria-label={isOpen ? 'Collapse' : 'Expand'}
              >
                {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              </button>
            ) : (
              <span className="w-4 h-4 inline-block" />
            )}
            {hasChildren
              ? <Folder className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
              : <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
            <span className="flex-1 truncate capitalize">{node.name}</span>
            <span className="text-[11px] text-slate-400 ml-1 flex-shrink-0">{node.count}</span>
          </div>
        );
      })}
    </div>
  );
}
